import type React from 'react';
import { ChevronLeft } from 'lucide-react';
import { Link } from 'react-router';
import { useI18nContext } from '@/i18n/i18n-react';
import { StoreSwitcherTrigger } from './store-switcher-trigger';

export interface MobilePageHeaderProps {
  /** Large title (Phases 2–4). Without it, only the store row. */
  title?: React.ReactNode;
  /** Actions on the right (icon buttons with aria-label, min-h-touch). */
  actions?: React.ReactNode;
  /** Shows a 44px back link instead of the store switcher. */
  backTo?: string;
  onOpenStoreSwitcher: () => void;
  storeTriggerRef?: React.Ref<HTMLButtonElement>;
}

/** Header of the mobile shell (< 768px): active store, or back, plus actions. */
export function MobilePageHeader({
  title,
  actions,
  backTo,
  onOpenStoreSwitcher,
  storeTriggerRef,
}: MobilePageHeaderProps) {
  const { LL } = useI18nContext();

  return (
    <header className="shrink-0 border-b bg-background px-4">
      <div className="flex min-h-touch items-center gap-2 py-1">
        {backTo ? (
          <Link
            to={backTo}
            aria-label={LL.shell.back()}
            className="-ml-3 flex min-h-touch min-w-touch items-center justify-center rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ChevronLeft className="size-6" />
          </Link>
        ) : (
          <StoreSwitcherTrigger
            ref={storeTriggerRef}
            onOpen={onOpenStoreSwitcher}
          />
        )}
        {actions && (
          <div className="ml-auto flex shrink-0 items-center gap-1">
            {actions}
          </div>
        )}
      </div>
      {title && (
        <h1 className="pb-3 text-2xl font-bold tracking-tight">{title}</h1>
      )}
    </header>
  );
}
